class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.currentProduct = null;
    this.currentVariant = null;
    this.quantity = 1;
    this.imageSwiper = null;
    this.currentCollectionHandle = null;
    this.modal = null;
    this.collectionCache = new Map();
    this.productCache = new Map();
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
  }

  connectedCallback() {
    this.modal = this;
    this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this.imageSwiper) {
      this.imageSwiper.destroy(true, true);
      this.imageSwiper = null;
    }
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);

    this.clearCache();
  }

  clearCache() {
    this.productCache.clear();
    this.collectionCache.clear();
  }

  getLangPrefix() {
    const pathParts = window.location.pathname.split('/');
    return pathParts[1] && pathParts[1].length === 2 ? `/${pathParts[1]}` : '';
  }

  handleDocumentClick(e) {
    const quickAddBtn = e.target.closest('.quick-add-button');
    const quickAddBtnAlt = e.target.closest('.quick-add-button-alt');

    if (quickAddBtn || quickAddBtnAlt) {
      e.preventDefault();
      e.stopPropagation();
      this.handleQuickAdd(quickAddBtn || quickAddBtnAlt);
    }
  }

  handleDocumentKeydown(e) {
    if (e.key === 'Escape' && this.modal && !this.modal.classList.contains('hidden')) {
      this.closeModal();
    }
  }

  setupEventListeners() {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);

    this.addEventListener('click', (e) => {
      const target = e.target;

      if (target.closest('.quick-view-modal__close') ||
        target.closest('.quick-view-modal__overlay') ||
        (target.classList.contains('quick-view-modal') && !target.closest('.quick-view-modal__content'))) {
        this.closeModal();
        return;
      }

      if (target.closest('.quick-view-modal__thumbnail')) {
        this.selectImage(target.closest('.quick-view-modal__thumbnail'));
      } else if (target.closest('.quick-view-modal__add-to-cart')) {
        e.preventDefault();
        this.addToCart();
      }
    });

    this.addEventListener('change', (e) => {
      if (e.target.matches('.quick-view-modal__variant-select')) {
        this.selectVariantFromDropdown(e.target);
      }
    });
  }

  async handleQuickAdd(button) {
    const productHandle = button.dataset.productHandle;
    const variantId = button.dataset.variantId;

    try {
      const productData = await this.fetchProductData(productHandle);

      if (productData && productData.variants && productData.variants.length === 1) {
        this.quickAddToCart(variantId, button, productData);
      } else {
        this.openModal(button);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      this.openModal(button);
    }
  }

  quickAddToCart(variantId, button, productData) {
    const originalContent = button.innerHTML;
    button.disabled = true;

    if (button.classList.contains('quick-add-button-alt')) {
      button.style.opacity = '0.5';
    } else {
      button.innerHTML = '<span class="quick-add-text">Adding...</span>';
    }
    const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
    const cartItem = { 
      id: variantId, 
      quantity: 1,
      properties: {
        '_source': 'Quick Buy'
      }
    };
    const previousProduct = this.currentProduct;
    this.currentProduct = productData;
    this.currentVariant = variant;
    
    if (variant && this.isPreOrder(variant)) {
      const replenishmentDate = this.getReplenishmentDate();
      const klaviyoReplenishmentDate = this.getKlaviyoReplenishmentDate();
      
      cartItem.properties['Pre-order'] = 'Pre order item';
      
      if (replenishmentDate && replenishmentDate !== 'soon') {
        cartItem.properties['_replenishment date'] = replenishmentDate;
      }
      if (klaviyoReplenishmentDate && klaviyoReplenishmentDate !== 'soon') {
        cartItem.properties['_klaviyo_replenishment_date'] = klaviyoReplenishmentDate;
      }
    }
    this.currentProduct = previousProduct;
    this.currentVariant = null;
    cartAddItem(
      cartItem,
      (response) => {
        if (typeof Vanga !== 'undefined') {
          Vanga.open();
        }

        button.innerHTML = originalContent;
        button.style.opacity = '';
        button.disabled = false;
      },
      (error) => {
        console.error('Error adding to cart:', error);
        alert('Failed to add item to cart. Please try again.');

        button.innerHTML = originalContent;
        button.style.opacity = '';
        button.disabled = false;
      }
    );
  }

  async openModal(button) {
    const productHandle = button.dataset.productHandle;
    const variantId = button.dataset.variantId;

    if (!productHandle) {
      console.error('Product handle not found');
      return;
    }

    this.showModal();
    this.showLoading();

    try {
      const productData = await this.fetchProductData(productHandle);

      if (!productData) {
        console.error('Failed to fetch product data');
        this.closeModal();
        return;
      }

      const collectionHandle = this.extractCollectionFromProduct(productData);

      this.currentProduct = productData;
      this.currentVariant = this.findVariantById(productData, variantId) || productData.variants[0];
      this.sizeChosen = false;
      this.quantity = 1;
      this.currentCollectionHandle = collectionHandle;

      this.populateModal(productData, collectionHandle);
      this.hideLoading();

    } catch (error) {
      console.error('Error opening quick view modal:', error);
      this.closeModal();
    }
  }

  async fetchProductData(productHandle) {
    if (this.productCache.has(productHandle)) {
      return this.productCache.get(productHandle);
    }

    try {
      const langPrefix = this.getLangPrefix();
      const fetchUrl = `${langPrefix}/products/${productHandle}?view=quickbuy`;      
      const response = await fetch(fetchUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.quickbuy-content');
      if (!content) {
        throw new Error('Quickbuy content not found');
      }
      const jsonScript = content.querySelector('.js-quickbuy-product-data');
      const productData = jsonScript ? JSON.parse(jsonScript.textContent) : null;    
      if (!productData) {
        throw new Error('Failed to parse product data');
      }
      productData.htmlContent = content.innerHTML;
      const fullTitle = content.querySelector('.quick-view-modal__title')?.textContent?.trim() || '';
      productData.title = fullTitle.split('|')[0].trim();
      
      // Extract country information
      productData.countryCode = content.dataset.countryCode;
      productData.inUsa = content.dataset.inUsa === 'true';
      productData.inUk = content.dataset.inUk === 'true';
      productData.inEu = content.dataset.inEu === 'true';
      
      this.productCache.set(productHandle, productData);

      return productData;
    } catch (error) {
      console.error('Error fetching product data:', error);
      return null;
    }
  }

  extractCollectionFromProduct(productData) {
    if (!productData) return null;
    
    const template = (productData.template || '').toLowerCase();
    
    const templatePatterns = [
      { pattern: /low-top/i, collection: 'low-top' },
      { pattern: /high-top/i, collection: 'high-top' },
      { pattern: /split-sole/i, collection: 'split-sole' },
      { pattern: /wedge/i, collection: 'wedge' }
    ];
        for (const { pattern, collection } of templatePatterns) {
      if (pattern.test(template)) {
        return collection;
      }
    }
    return null;
  }

  findVariantById(product, variantId) {
    return product.variants.find(variant => variant.id.toString() === variantId.toString());
  }

  populateModal(product, collectionHandle) {
    const titleContainer = this.querySelector('.quick-view-modal__header');
    const ratingContainer = this.querySelector('.quick-view-modal__rating');
    
    if (product.htmlContent) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(product.htmlContent, 'text/html');
      
      const titleEl = doc.querySelector('.quick-view-modal__title');
      if (titleContainer && titleEl) {
        titleContainer.innerHTML = titleEl.outerHTML;
      }
      
      const ratingEl = doc.querySelector('.quick-view-modal__rating');
      if (ratingContainer && ratingEl) {
        ratingContainer.innerHTML = ratingEl.innerHTML;
        
        if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
          window.StampedFn.loadBadges();
        }
      }
    }
    
    const langPrefix = this.getLangPrefix();
    this.setElementAttribute('quick-view-modal__view-product', 'href', `${langPrefix}/products/${product.handle}`);

    this.populateImageSwiper(product.images, product.title);

    this.populateThumbnails(product.images);

    this.populatePrice(product);

    this.populateVariants(product);

    this.populateCollectionGrid(product, collectionHandle);

    this.updatePreOrderBadge();

    this.updateAddToCartButton();
  }

  setElementContent(className, content) {
    const element = this.querySelector(`.${className}`);
    if (element) {
      element.textContent = content;
    }
  }

  setElementAttribute(className, attribute, value) {
    const element = this.querySelector(`.${className}`);
    if (element) {
      element.setAttribute(attribute, value);
    }
  }

  populateImageSwiper(images, productTitle) {
    const swiperWrapper = this.querySelector('.swiper-wrapper');
    if (!swiperWrapper || !images || images.length === 0) return;

    if (this.imageSwiper) {
      this.imageSwiper.destroy(true, true);
      this.imageSwiper = null;
    }

    swiperWrapper.innerHTML = images.map((image, index) => `
      <div class="swiper-slide">
        <img
          src="${this.getImageUrl(image, 540)}"
          alt="${productTitle} - Image ${index + 1}"
          loading="${index === 0 ? 'eager' : 'lazy'}"
        >
      </div>
    `).join('');

    this.initializeSwiper();
  }

  initializeSwiper() {
    if (typeof Swiper === 'undefined') {
      console.error('Swiper is not loaded');
      return;
    }

    const swiperElement = this.querySelector('.quick-view-image-swiper');
    if (!swiperElement) return;

    this.imageSwiper = new Swiper(swiperElement, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      speed: 300,
      centeredSlides: false,
      navigation: {
        nextEl: this.querySelector('.quick-view-modal__arrow--next'),
        prevEl: this.querySelector('.quick-view-modal__arrow--prev'),
      },
      scrollbar: {
        el: this.querySelector('.quick-view-swiper-scrollbar'),
        draggable: true,
        hide: false,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      mousewheel: false,
      touchRatio: 1,
      grabCursor: true,
      breakpoints: {
        0: {
          slidesPerView: 1.4,
          spaceBetween: 5,
          centeredSlides: false,
        },
        769: {
          slidesPerView: 1,
          spaceBetween: 0,
          centeredSlides: false,
        }
      },
      on: {
        slideChange: () => {
          this.updateActiveThumbnail(this.imageSwiper.activeIndex);
        }
      }
    });
  }

  updateActiveThumbnail(index) {
    const thumbnails = this.querySelectorAll('.quick-view-modal__thumbnail');
    thumbnails.forEach(t => t.classList.remove('active'));

    if (thumbnails[index]) {
      thumbnails[index].classList.add('active');
    }
  }

  populateThumbnails(images) {
    const container = this.querySelector('.quick-view-modal__thumbnails');
    if (!container || !images || images.length === 0) return;

    container.innerHTML = images.map((image, index) => `
      <div class="quick-view-modal__thumbnail ${index === 0 ? 'active' : ''}" data-image-index="${index}">
        <img
          src="${this.getImageUrl(image, 100)}"
          alt="Product image ${index + 1}"
          loading="lazy"
        >
      </div>
    `).join('');
  }

  populateRating(product) {
    const container = this.querySelector('.quick-view-modal__rating');
    if (!container) return;

    container.innerHTML = `
      <span class="stamped-product-reviews-badge stamped-main-badge" 
            data-id="${product.id}" 
            data-product-sku="${product.handle}" 
            data-product-type="${product.type || ''}" 
            data-product-title="${product.title}">
      </span>
    `;

    if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
      window.StampedFn.loadBadges();
    }
  }

  populatePrice(product) {
    const container = this.querySelector('.quick-view-modal__price');
    if (!container || !this.currentVariant) return;

    const priceVariant = this.sizeChosen ? this.currentVariant : this.getDefaultPriceVariant(product);
    const price = priceVariant.price;
    const compareAtPrice = priceVariant.compare_at_price;
    
    const isPartOfSale = product.discountEnabled && !product.saleGlobalDiscountExcluded;
    
    let priceHTML = '';

    if (isPartOfSale) {
      const discountPercentage = product.discountPercentage || 0;
      const saleMultiplier = (100 - discountPercentage) / 100;
      const discountedPrice = price * saleMultiplier;
      
      const formattedOriginalPrice = this.formatPrice(price);
      const formattedDiscountedPrice = this.formatPrice(discountedPrice);
      const discountColor = product.discountColor || '#10b981';
      
      priceHTML = `
        <div class="price__sale sale-discount">
          <span style="color: #999999;">
            <s class="price-item price-item--regular">${formattedOriginalPrice}</s>
          </span>
          <span class="price-item price-item--sale price-item--last">${formattedDiscountedPrice}</span>
          <span class="percentage-off" style="color: ${discountColor};">
            ${discountPercentage}% off
          </span>
        </div>
      `;
    } else if (compareAtPrice && compareAtPrice > price) {
      const formattedPrice = this.formatPrice(price);
      const formattedComparePrice = this.formatPrice(compareAtPrice);
      priceHTML = `
        <span class="quick-view-modal__price quick-view-modal__price--sale">${formattedPrice}</span>
        <span class="quick-view-modal__price-compare">${formattedComparePrice}</span>
      `;
    } else {
      const formattedPrice = this.formatPrice(price);
      priceHTML = `<span class="quick-view-modal__price">${formattedPrice}</span>`;
    }

    container.innerHTML = priceHTML;
  }

  populateVariants(product) {
    const container = this.querySelector('.quick-view-modal__variants');
    if (!container || !product.options?.length) return;

    container.innerHTML = product.options.map((option, optionIndex) => {
      const optionsHTML = option.values.map(value => {
        const variant = product.variants.find(v => v.options[optionIndex] === value);
        const isAvailable = variant && variant.available;

        let disabled = '';
        let optionValue = value;

        // Get country-specific size display
        if (variant) {
          if (product.inUsa && variant.variant_usa_size) {
            optionValue = variant.variant_usa_size;
          } else if (product.inUk && variant.variant_uk_size) {
            optionValue = variant.variant_uk_size;
          } else if (product.inEu && variant.variant_eu_size) {
            optionValue = variant.variant_eu_size;
          }
        }

        if (!isAvailable) {
          disabled = ' disabled';
        } 
          

        return `<option value="${value}"${disabled}>${optionValue}</option>`;
      }).join('');

      return `
        <div class="quick-view-modal__variant-group">
          <div class="quick-view-modal__variant-dropdown">
            <select class="quick-view-modal__variant-select" data-option-index="${optionIndex}">
              <option value="" disabled selected>Select a Size</option>
              ${optionsHTML}
            </select>
            <span class="quick-view-modal__variant-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="8" viewBox="0 0 13 8" fill="none">
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.5645 0.69259C12.4994 0.627401 12.4222 0.575681 12.3371 0.540392C12.2521 0.505103 12.1609 0.486938 12.0689 0.486938C11.9768 0.486938 11.8856 0.505103 11.8006 0.540392C11.7155 0.575681 11.6383 0.627401 11.5733 0.69259L6.46885 5.79839L1.36445 0.69259C1.23301 0.561149 1.05474 0.487306 0.868853 0.487306C0.682967 0.487306 0.504694 0.561149 0.373253 0.69259C0.241812 0.824031 0.167969 1.0023 0.167969 1.18819C0.167969 1.37408 0.241812 1.55235 0.373253 1.68379L5.97325 7.28379C6.03828 7.34898 6.11552 7.4007 6.20057 7.43599C6.28561 7.47128 6.37678 7.48944 6.46885 7.48944C6.56093 7.48944 6.6521 7.47128 6.73714 7.43599C6.82218 7.4007 6.89943 7.34898 6.96445 7.28379L12.5645 1.68379C12.6296 1.61877 12.6814 1.54152 12.7167 1.45648C12.7519 1.37143 12.7701 1.28026 12.7701 1.18819C12.7701 1.09612 12.7519 1.00495 12.7167 0.919903C12.6814 0.83486 12.6296 0.757614 12.5645 0.69259Z" fill="black"/>
</svg>
            </span>
            <div class="quick-view-modal__error-message">
              Please select a size.
            </div>
          </div>
        </div>
      `;
    }).join('');
  }

  async populateCollectionGrid(product, collectionHandle) {
    const container = this.querySelector('.quick-view-modal__collection-grid');
    if (!container || !collectionHandle) {
      if (container) container.innerHTML = '';
      return;
    }

    try {
      let collectionData;

      if (this.collectionCache.has(collectionHandle)) {
        collectionData = this.collectionCache.get(collectionHandle);
      } else {
        const langPrefix = this.getLangPrefix();
        const response = await fetch(`${langPrefix}/collections/${collectionHandle}/products.json`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const { products } = await response.json();
        const title = collectionHandle.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        collectionData = { title, products };
        this.collectionCache.set(collectionHandle, collectionData);
      }

      this.renderCollectionGrid(container, collectionData, product);
    } catch (error) {
      console.error('Error fetching collection:', collectionHandle, error);
      container.innerHTML = '';
    }
  }

  renderCollectionGrid(container, collection, currentProduct) {
    const currentColorName = currentProduct.title.split(' ')[0];

    container.innerHTML = `
      <div class="quick-view-modal__collection-title">
        Collection: ${collection.title}
      </div>
      <div class="quick-view-modal__collection-label">
        Color: ${currentColorName}
      </div>
      <ul class="quick-view-modal__collection-list">
        ${collection.products ? collection.products.map((relatedProduct, index) => {
      const imageUrl = this.getProductImage(relatedProduct);
      return `
          <li class="quick-view-modal__collection-item ${relatedProduct.id === currentProduct.id ? 'current' : ''}" 
              data-product-id="${relatedProduct.id}" 
              data-product-handle="${relatedProduct.handle}"
              data-product-title="${relatedProduct.title}">
            <div class="quick-view-modal__collection-image">
              <img src="${this.getImageUrl(imageUrl, 120)}" 
                   alt="${relatedProduct.title}" 
                   loading="lazy">
            </div>
            
          </li>
        `;
    }).join('') : ''}
      </ul>
    `;

    this.setupCollectionItemListeners();
  }

  setupCollectionItemListeners() {
    const collectionItems = this.querySelectorAll('.quick-view-modal__collection-item');
    const colorLabel = this.querySelector('.quick-view-modal__collection-label');
    const currentItem = this.querySelector('.quick-view-modal__collection-item.current');
    const currentColorName = currentItem ? currentItem.dataset.productTitle.split(' ')[0] : '';

    collectionItems.forEach(item => {
      item.addEventListener('click', () => {
        if (item.classList.contains('current')) {
          return;
        }

        const productHandle = item.dataset.productHandle;
        if (productHandle) {
          item.classList.add('loading');
          this.switchToProduct(productHandle);
        }
      });

      item.addEventListener('mouseenter', () => {
        const productTitle = item.dataset.productTitle;
        if (colorLabel && productTitle) {
          const colorName = productTitle.split(' ')[0];
          colorLabel.textContent = `Color: ${colorName}`;
        }
      });

      item.addEventListener('mouseleave', () => {
        if (colorLabel && currentColorName) {
          colorLabel.textContent = `Color: ${currentColorName}`;
        }
      });
    });
  }

  async switchToProduct(productHandle) {
    try {
      const productData = await this.fetchProductData(productHandle);

      if (!productData) {
        console.error('Failed to fetch product data');
        return;
      }

      this.currentProduct = productData;
      this.currentVariant = productData.variants[0];
      this.sizeChosen = false;
      this.quantity = 1;

      // Inject the translated HTML content for title and rating
      const titleContainer = this.querySelector('.quick-view-modal__header');
      const ratingContainer = this.querySelector('.quick-view-modal__rating');
      
      if (productData.htmlContent) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(productData.htmlContent, 'text/html');
        
        // Inject translated title
        const titleEl = doc.querySelector('.quick-view-modal__title');
        if (titleContainer && titleEl) {
          titleContainer.innerHTML = titleEl.outerHTML;
        }
        
        // Inject translated rating badge
        const ratingEl = doc.querySelector('.quick-view-modal__rating');
        if (ratingContainer && ratingEl) {
          ratingContainer.innerHTML = ratingEl.innerHTML;
          
          // Reload Stamped badges
          if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
            window.StampedFn.loadBadges();
          }
        }
      }
      
      // Include language prefix in product detail link
      const langPrefix = this.getLangPrefix();
      this.setElementAttribute('quick-view-modal__view-product', 'href', `${langPrefix}/products/${productData.handle}`);

      this.populateImageSwiper(productData.images, productData.title);
      this.populateThumbnails(productData.images);
      this.populatePrice(productData);
      this.populateVariants(productData);
      this.updatePreOrderBadge();
      this.updateAddToCartButton();

      this.updateCurrentItemIndicator(productData.id);

    } catch (error) {
      console.error('Error switching to product:', error);
    } finally {
      this.querySelectorAll('.quick-view-modal__collection-item').forEach(item => {
        item.classList.remove('loading');
      });
    }
  }

  updateCurrentItemIndicator(productId) {
    this.querySelectorAll('.quick-view-modal__collection-item').forEach(item => {
      item.classList.remove('current');
    });

    const currentItem = this.querySelector(`[data-product-id="${productId}"]`);
    if (currentItem) {
      currentItem.classList.add('current');

      const colorLabel = this.querySelector('.quick-view-modal__collection-label');
      if (colorLabel && currentItem.dataset.productTitle) {
        const colorName = currentItem.dataset.productTitle.split(' ')[0];
        colorLabel.textContent = `Color: ${colorName}`;
      }
    }
  }

  getProductImage(product) {
    if (product.featured_image) {
      return product.featured_image;
    }

    if (product.image) {
      return product.image;
    }

    if (product.images && product.images.length > 0) {
      return product.images[0].src || product.images[0];
    }

    if (product.featured_media && product.featured_media.preview_image) {
      return product.featured_media.preview_image.src;
    }

    if (product.media && product.media.length > 0 && product.media[0].preview_image) {
      return product.media[0].preview_image.src;
    }
  }

  getImageUrl(url, width) {
    if (!url) return '';
    return url.includes('?') ? url : `${url}?width=${width}`;
  }

  selectImage(thumbnail) {
    const index = parseInt(thumbnail.dataset.imageIndex);

    if (this.imageSwiper) {
      this.imageSwiper.slideTo(index);
    }

    this.updateActiveThumbnail(index);
  }

  selectVariantFromDropdown(select) {
    const optionIndex = parseInt(select.dataset.optionIndex);
    const value = select.value;

    if (!value) return;

    this.hideSizeError();

    this.updateVariantSelection(optionIndex, value);
  }

  updateVariantSelection(optionIndex, value) {
    const newVariant = this.currentProduct.variants.find(variant => {
      return variant.options[optionIndex] === value;
    });

    if (newVariant) {
      this.currentVariant = newVariant;
      this.sizeChosen = true;
      this.updatePrice();
      this.updatePreOrderBadge();
      this.updateAddToCartButton();
    }
  }

  // Mirrors snippets/price.liquid: with no size chosen, show the highest price offered. On a tie,
  // prefer a size that isn't discounted. Only available sizes count, unless none are.
  getDefaultPriceVariant(product) {
    const variants = product.variants || [];
    const available = variants.filter(variant => variant.available);
    const candidates = available.length ? available : variants;
    const isDiscounted = (variant) => variant.compare_at_price > variant.price;

    return candidates.reduce((target, candidate) => {
      if (candidate.price > target.price) return candidate;
      if (candidate.price === target.price && isDiscounted(target) && !isDiscounted(candidate)) return candidate;
      return target;
    }, candidates[0] || this.currentVariant);
  }

  updatePrice() {
    this.populatePrice(this.currentProduct);
  }

  isPreOrder(variant) {
    if (!variant || !this.currentProduct) return false;

    const noPreOrderTags = (this.currentProduct.tags || []).filter(tag =>
      tag.toLowerCase().startsWith('no_pre_order:')
    );

    const excludedVariants = noPreOrderTags.map(tag => {
      const variantName = tag.split('no_pre_order:')[1]?.trim();
      return variantName ? variantName.toLowerCase() : '';
    });

    const variantOption1 = (variant.option1 || '').toLowerCase();
    const isExcluded = excludedVariants.some(excluded =>
      excluded === 'all' || excluded === variantOption1
    );

    if (isExcluded) return false;

    return variant.inventory_quantity <= 0 && variant.available;
  }

  updatePreOrderBadge() {
    if (!this.currentVariant || !this.currentProduct) return;

    const preOrderWrapper = this.querySelector('.pre-order-discount-wraper');
    const preOrderDiscountMessage = this.querySelector('.pre-order-discount-message');
    const preOrderMessage = this.querySelector('.pre-order-message');
    const sizeSelect = this.querySelector('.quick-view-modal__variant-select');

    const sizeSelected = sizeSelect ? sizeSelect.value : false;
    const isPreOrder = this.isPreOrder(this.currentVariant);

    if (isPreOrder && sizeSelected) {
      if (preOrderDiscountMessage) {
        preOrderDiscountMessage.textContent = 'Pre-order';
      }

      if (preOrderWrapper) {
        preOrderWrapper.classList.remove('hidden');
      }
      if (preOrderMessage) {
        const replenishmentDate = this.getReplenishmentDate();
        const message = `Enjoy VIP access by pre-ordering today! This item flew off the shelves — shop now to skip the line and guarantee your pair. Ships by ${replenishmentDate}.`;
        
        preOrderMessage.textContent = message;
        preOrderMessage.classList.remove('hidden');
      }
    } else {
      if (preOrderWrapper) {
        preOrderWrapper.classList.add('hidden');
      }
      if (preOrderMessage) {
        preOrderMessage.classList.add('hidden');
        preOrderMessage.textContent = '';
      }
    }
  }

  getReplenishmentDate() {
    if (!this.currentVariant || !this.currentProduct) return 'soon';

    const isUSA = this.currentProduct.inUsa || false;

    const dateKey = isUSA ? 'replenishment_date_usa' : 'replenishment_date_global';
    const replenishmentDate = this.currentVariant[dateKey];

    if (!replenishmentDate || replenishmentDate === 'soon' || replenishmentDate === 'null') {
      return 'soon';
    }

    return replenishmentDate;
  }

  getKlaviyoReplenishmentDate() {
    if (!this.currentVariant || !this.currentProduct) return 'soon';

    const isUSA = this.currentProduct.inUsa || false;

    const dateKey = isUSA ? 'replenishment_date_usa' : 'replenishment_date_global';
    const replenishmentDate = this.currentVariant[dateKey];
    
    if (!replenishmentDate || replenishmentDate === 'soon' || replenishmentDate === 'null') {
      return 'soon';
    }

    try {
      const date = new Date(replenishmentDate);
      if (isNaN(date.getTime())) return 'soon';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error('Error converting replenishment date:', e);
      return 'soon';
    }
  }

  updateAddToCartButton() {
    const button = this.querySelector('.quick-view-modal__add-to-cart');
    if (!button) return;

    const sizeSelect = this.querySelector('.quick-view-modal__variant-select');
    const sizeSelected = sizeSelect ? sizeSelect.value : false;

    const allVariantsUnavailable = this.currentProduct.variants.every(variant => !variant.available);

    const btnText = button.querySelector('.btn__text');
    const textAddToCart = button.dataset.textAddToCart || 'Add to Cart';
    const textPreOrder = button.dataset.textPreOrder || 'Pre-Order';
    const textSoldOut = button.dataset.textSoldOut || 'Sold Out';

    if (allVariantsUnavailable) {
      button.disabled = true;
      btnText.textContent = textSoldOut;
    } else if (this.currentVariant && this.isPreOrder(this.currentVariant) && sizeSelected) {
      button.disabled = false;
      btnText.textContent = textPreOrder;
    } else {
      button.disabled = false;
      btnText.textContent = textAddToCart;
    }
  }

  addToCart() {
    const sizeSelect = this.querySelector('.quick-view-modal__variant-select');
    if (sizeSelect && !sizeSelect.value) {
      this.showSizeError();
      return;
    }

    if (!this.currentVariant?.available) {
      console.error('Cannot add unavailable variant to cart');
      return;
    }

    const button = this.querySelector('.quick-view-modal__add-to-cart');
    const text = button.querySelector('.btn__text');
    const loading = button.querySelector('.btn__loading');

    text.classList.add('hidden');
    loading.classList.remove('hidden');
    button.disabled = true;
    const cartItem = { 
      id: this.currentVariant.id, 
      quantity: this.quantity,
      properties: {
        '_source': 'Quick Buy'
      }
    };
    
    if (this.isPreOrder(this.currentVariant)) {
      const replenishmentDate = this.getReplenishmentDate();
      const klaviyoReplenishmentDate = this.getKlaviyoReplenishmentDate();
      
      cartItem.properties['Pre-order'] = 'Pre order item';
      
      if (replenishmentDate && replenishmentDate !== 'soon') {
        cartItem.properties['_replenishment date'] = replenishmentDate;
      }
      if (klaviyoReplenishmentDate && klaviyoReplenishmentDate !== 'soon') {
        cartItem.properties['_klaviyo_replenishment_date'] = klaviyoReplenishmentDate;
      }
    }
    cartAddItem(
       cartItem,
      (response) => {
        if (typeof Vanga !== 'undefined') {
          Vanga.open();
        }

        this.closeModal();

        text.classList.remove('hidden');
        loading.classList.add('hidden');
        button.disabled = false;
      },
      (error) => {
        console.error('Error adding to cart:', error);
        alert('Failed to add item to cart. Please try again.');

        text.classList.remove('hidden');
        loading.classList.add('hidden');
        button.disabled = false;
      }
    );
  }

  showSizeError() {
    const errorMessage = this.querySelector('.quick-view-modal__error-message');
    if (errorMessage) {
      errorMessage.classList.add('visible');
    }
  }

  hideSizeError() {
    const errorMessage = this.querySelector('.quick-view-modal__error-message');
    if (errorMessage) {
      errorMessage.classList.remove('visible');
    }
  }

  formatPrice(price) {
    const formattedPrice = (price / 100).toFixed(2);
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';

    let returnStr = moneyFormat;
    if(returnStr.includes('{{amount}}')) {
      returnStr = returnStr.replace('{{amount}}', formattedPrice);
    } else if (returnStr.includes('{{amount_with_comma_separator}}')) {
      returnStr = returnStr.replace('{{amount_with_comma_separator}}', formattedPrice);
    } else if (returnStr.includes('{{amount_no_decimals_with_comma_separator}}')) {
      returnStr = returnStr.replace('{{amount_no_decimals_with_comma_separator}}', formattedPrice);
    } else if (returnStr.includes('{{amount_with_space_separator}}')) {
      returnStr = returnStr.replace('{{amount_with_space_separator}}', formattedPrice);
    } else if (returnStr.includes('{{amount_no_decimals_with_space_separator}}')) {
      returnStr = returnStr.replace('{{amount_no_decimals_with_space_separator}}', formattedPrice);
    } else if (returnStr.includes('{{amount_with_apostrophe_separator}}')) {
      returnStr = returnStr.replace('{{amount_with_apostrophe_separator}}', formattedPrice);
    }
    
    return returnStr;
  }

  formatTitle(title) {
    if (title.includes('Derek Hough')) {
      const titleAfter = title.replace('Derek Hough', '').trim();
      if (titleAfter) {
        return `Derek Hough | ${titleAfter}`;
      }
      return 'Derek Hough';
    }

    if (title.includes('Jade')) {
      const parts = title.split(' ');
      if (parts.length > 2 && parts[1] === 'Jade') {
        return `${parts[0]} ${parts[1]} | ${parts.slice(2).join(' ')}`;
      }
      return title;
    }

    const parts = title.split(' ');
    if (parts.length > 1) {
      return `${parts[0]} | ${parts.slice(1).join(' ')}`;
    }
    return title;
  }

  showModal() {
    if (this.modal) {
      this.modal.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.classList.add('hidden');
      document.body.style.overflow = '';
    }

    if (this.imageSwiper) {
      this.imageSwiper.destroy(true, true);
      this.imageSwiper = null;
    }

    this.hideLoading();
  }

  showLoading() {
    const loading = this.querySelector('.quick-view-modal__loading');
    const body = this.querySelector('.quick-view-modal__body');
    if (loading) loading.classList.remove('hidden');
    if (body) body.classList.add('loading');
  }

  hideLoading() {
    const loading = this.querySelector('.quick-view-modal__loading');
    const body = this.querySelector('.quick-view-modal__body');
    if (loading) loading.classList.add('hidden');
    if (body) body.classList.remove('loading');
  }
}

if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!document.querySelector('quick-view-modal')) {
      document.body.appendChild(document.createElement('quick-view-modal'));
    }
  });
} else {
  if (!document.querySelector('quick-view-modal')) {
    document.body.appendChild(document.createElement('quick-view-modal'));
  }
}
